import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  TextField,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import ReceiptIcon from '@mui/icons-material/Receipt';

import {
  useGetPurchaseOrderNoteQuery,
  useUpdatePurchaseOrderNoteStatusMutation,
} from '../../../store/api/purchaseApi';
import { exportPurchaseOrderPdf, sharePurchaseOrderPdf } from '../utils/exportPurchaseOrderPdf';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function PurchaseOrderNoteDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

  const { data: poNote, isLoading, error } = useGetPurchaseOrderNoteQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();
  const [updateStatus, { isLoading: updatingStatus }] = useUpdatePurchaseOrderNoteStatusMutation();

  const currency = company?.currency || 'USD';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';

  const formatAmount = (v) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
        maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
      }).format(Number(v || 0));
    } catch (e) {
      return `${currency} ${Number(v || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    switch (dateFormat) {
      case 'MM/dd/yyyy':
        return `${mm}/${dd}/${yyyy}`;
      case 'yyyy-MM-dd':
        return `${yyyy}-${mm}-${dd}`;
      case 'dd-MM-yyyy':
        return `${dd}-${mm}-${yyyy}`;
      case 'dd/MM/yyyy':
      default:
        return `${dd}/${mm}/${yyyy}`;
    }
  };

  const getStatusChipColor = (status) => {
    const map = {
      draft: 'default',
      sent: 'info',
      confirmed: 'primary',
      converted: 'success',
      cancelled: 'error',
    };
    return map[status] || 'default';
  };

  const handleStatusChange = async (newStatus) => {
    if (!newStatus) return;
    try {
      await updateStatus({ id, status: newStatus }).unwrap();
    } catch (err) {
      alert(err?.data?.message || 'Failed to update status');
    }
  };

  if (isLoading) return <Typography>Loading purchase order...</Typography>;
  if (error || !poNote) return <Typography color="error">Purchase order not found</Typography>;

  const isConverted = poNote.status === 'converted';
  const canConvert = !['converted', 'cancelled'].includes(poNote.status);
  const canEdit = !isConverted;

  return (
    <Box>
      {/* Top Action Bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/purchases/order-notes')}
            sx={{ borderRadius: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            PO: {poNote.poNumber}
          </Typography>
          <Chip
            label={poNote.status}
            color={getStatusChipColor(poNote.status)}
            size="small"
            sx={{ textTransform: 'capitalize', fontWeight: 600 }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Quick status dropdown */}
          {!isConverted && (
            <TextField
              select
              size="small"
              label="Status"
              value={poNote.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={updatingStatus}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
          )}

          {canConvert && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ShoppingCartCheckoutIcon />}
              onClick={() => navigate(`/purchases/orders/new?fromPoId=${id}`)}
              sx={{ borderRadius: 2 }}
            >
              Convert to Purchase
            </Button>
          )}

          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/purchases/order-notes/${id}/edit`)}
              sx={{ borderRadius: 2 }}
            >
              Edit PO
            </Button>
          )}

          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PrintIcon />}
            onClick={() => exportPurchaseOrderPdf(poNote, company)}
            sx={{ borderRadius: 2 }}
          >
            Print PDF
          </Button>

          <Button
            variant="outlined"
            color="info"
            startIcon={<ShareIcon />}
            onClick={() => sharePurchaseOrderPdf(poNote, company)}
            sx={{ borderRadius: 2 }}
          >
            Share
          </Button>
        </Box>
      </Box>

      {/* Converted Alert Banner */}
      {isConverted && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          <strong>Purchase Order Converted:</strong> This order has been converted into an actual
          Purchase Invoice.{' '}
          {poNote.convertedPurchase && (
            <Button
              size="small"
              variant="text"
              startIcon={<ReceiptIcon />}
              onClick={() => navigate(`/purchases/orders/${poNote.convertedPurchase.id}`)}
              sx={{ fontWeight: 600, ml: 1 }}
            >
              View Purchase ({poNote.convertedPurchase.invoiceNumber || poNote.convertedPurchase.orderNumber})
            </Button>
          )}
        </Alert>
      )}

      {/* Order Info Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Vendor / Supplier
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
                {poNote.supplier?.name || '-'}
              </Typography>
              {poNote.supplier?.phone && (
                <Typography variant="body2" color="text.secondary">
                  Phone: {poNote.supplier.phone}
                </Typography>
              )}
              {poNote.supplier?.email && (
                <Typography variant="body2" color="text.secondary">
                  Email: {poNote.supplier.email}
                </Typography>
              )}
              {poNote.supplier?.address && (
                <Typography variant="body2" color="text.secondary">
                  Address: {poNote.supplier.address}
                </Typography>
              )}
              {poNote.supplier?.trn && (
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                  TRN: {poNote.supplier.trn}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Order Details
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Order Date:</strong> {formatDate(poNote.orderDate)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Expected Delivery:</strong> {formatDate(poNote.expectedDeliveryDate)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Tax Status:</strong> {poNote.isTaxable ? `VAT Applied (${company?.vatRate || 0}%)` : 'Non-Taxable'}
                </Typography>
                {poNote.notes && (
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                    &ldquo;{poNote.notes}&rdquo;
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Order Totals
              </Typography>
              <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700, mt: 0.5 }}>
                {formatAmount(poNote.grandTotal)}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                  <Typography variant="body2">{formatAmount(poNote.subtotal)}</Typography>
                </Box>
                {Number(poNote.discountTotal) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="warning.main">Discount:</Typography>
                    <Typography variant="body2" color="warning.main">- {formatAmount(poNote.discountTotal)}</Typography>
                  </Box>
                )}
                {Number(poNote.vatTotal) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">VAT:</Typography>
                    <Typography variant="body2">{formatAmount(poNote.vatTotal)}</Typography>
                  </Box>
                )}
                {Number(poNote.roundOff) !== 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Round Off:</Typography>
                    <Typography variant="body2">{formatAmount(poNote.roundOff)}</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Items Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Product Name / SKU</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Qty</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Unit Cost</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Discount</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">VAT</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Line Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(poNote.items || []).map((item, idx) => (
                <TableRow key={item.id || idx} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.product?.name || item.name || '-'}
                    </Typography>
                    {item.product?.sku && (
                      <Typography variant="caption" color="text.secondary">
                        SKU: {item.product.sku}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {item.quantity}
                  </TableCell>
                  <TableCell>{item.unit || item.product?.unit || 'pcs'}</TableCell>
                  <TableCell align="right">{formatAmount(item.unitPrice)}</TableCell>
                  <TableCell align="right">{formatAmount(item.discount)}</TableCell>
                  <TableCell align="right">{formatAmount(item.vatAmount)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatAmount(item.total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default PurchaseOrderNoteDetail;
